import { useEffect, useState } from 'react'
import { fallbackEvents } from '../data/events'
import { listPublishedEvents } from '../lib/eventsService'
import { isSupabaseConfigured } from '../lib/supabase'
import type { RetreatEvent } from '../types/events'
import { isUpcoming, sortByDate, todayISO } from '../utils/eventDates'

interface PublicEventsState {
  status: 'loading' | 'ready'
  events: RetreatEvent[]
}

// One request per page load, shared by the hero strip and the events section.
let cached: Promise<RetreatEvent[]> | null = null

function loadPublicEvents(): Promise<RetreatEvent[]> {
  if (!cached) {
    const today = todayISO()
    const source = isSupabaseConfigured
      ? listPublishedEvents(today)
      : Promise.reject(new Error('Supabase not configured'))

    cached = source
      .catch(() => fallbackEvents.filter((event) => event.published))
      .then((events) => sortByDate(events.filter((event) => isUpcoming(event.eventDate, today))))
  }
  return cached
}

/** Call after an admin save so the public site shows the change right away. */
export function invalidatePublicEvents(): void {
  cached = null
}

export function usePublicEvents(): PublicEventsState {
  const [state, setState] = useState<PublicEventsState>({ status: 'loading', events: [] })

  useEffect(() => {
    let active = true
    loadPublicEvents().then((events) => {
      if (active) setState({ status: 'ready', events })
    })
    return () => {
      active = false
    }
  }, [])

  return state
}
